/**
 * TrayTicketExplainer — being rebuilt from scratch to duplicate
 * WinForms `frmDietech_Prd_TrayTicket_Explain.vb` exactly. Props
 * signature preserved so the parent `TrayTicketVisualizerDialog`
 * still mounts.
 *
 * WinForms reference:
 *   • Toolbar: Patient · Mealtime · MenuDate · MenuCycle info · Refresh
 *   • Left pane: dgr grid (menu items) + food-group/allergy scroller
 *   • Right pane: trl TreeList (step tree)
 *   • Core: ExecuteMenuRegeneration — 3-pass generator run
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import useMediaQuery from '@mui/material/useMediaQuery';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import DateField from '../../components/form/DateField';
import { useGlobalStore } from '../../stores/globalStore';
import { useTrayTicketPatientList, useTrayTicketMenuCycle, useTrayTicketExplainer, useRegenerateExplainer, trayTicketKeys, useTrayTicketColorConfig } from '../../api/trayTicketApi';
import { argbToHex } from '../../types/facility';
import { useQueryClient } from '@tanstack/react-query';
import Checkbox from '@mui/material/Checkbox';
import type { ExplainerRequest, ExplainerRegenerateRequest, ExplainerStepNode, ExplainerMenuItem, ExplainerResult } from '../../types/trayTicket';
import Button from '@mui/material/Button';
// react-resizable-panels v4 renamed `PanelGroup` → `Group` and
// `PanelResizeHandle` → `Separator`, and swapped `direction` for
// `orientation`. Aliasing back to the familiar names so the
// JSX reads clearly.
import {
  Group as PanelGroup,
  Panel,
  Separator as PanelResizeHandle,
} from 'react-resizable-panels';
import './TrayTicketExplainer.css';

const MEALTIME_DEFAULTS: readonly string[] = ['Breakfast', 'Lunch', 'Supper'];

function todayStr(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

interface TrayTicketExplainerProps {
  open: boolean;
  onClose: () => void;
  date: string;
  mealtime: number;
  fKey_Patient: number;
  patientName: string;
}

export default function TrayTicketExplainer({
  open,
  onClose,
  date,
  mealtime,
  fKey_Patient,
  patientName,
}: TrayTicketExplainerProps) {
  const facilityId = useGlobalStore((s) => s.facilityId);
  const facilitySettings = useGlobalStore((s) => s.facilitySettings);

  // Item-row rendering parity with the visualizer:
  //   • `useCaps` — WinForms `TT_UseCapsOnItems` uppercases the
  //     whole composed item / note line.
  //   • `useColor` — WinForms `Use_Color` gates per-item text
  //     color (from `TT_ItemColor` argb). When off, items render
  //     in the default text color regardless of the row value.
  // Cast + optional-chain because the shared FacilitySettings
  // shape here still exposes an untyped record for feature flags.
  const useCaps = !!(facilitySettings as Record<string, unknown> | null)?.tT_UseCapsOnItems;
  const colorConfig = useTrayTicketColorConfig();
  const useColor = !!colorConfig?.useColor;

  // Mobile / narrow layout — matches the visualizer breakpoint
  // (max-width 1199.95px). On narrow the inline toolbar hides
  // and a hamburger button opens a Drawer that hosts the
  // selectors (Patient / Mealtime / Date / MenuCycle info).
  // Refresh stays in the main toolbar regardless.
  const isNarrow = useMediaQuery('(max-width:1099.95px)');
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  useEffect(() => { if (!open) setMobileDrawerOpen(false); }, [open]);

  // Facility meal names for the mealtime dropdown — WinForms uses
  // `GetMainMealNames` which returns just the three main meals.
  // Fall back to English defaults when the facility hasn't
  // customized them.
  const facilityMealNames: readonly string[] = useMemo(() => {
    const s = facilitySettings as unknown as Record<string, unknown> | null;
    const raw = (s?.mainMealNames as string[] | undefined) ?? [];
    return raw.length > 0 ? raw : MEALTIME_DEFAULTS;
  }, [facilitySettings]);

  // Toolbar state — seeded from the visualizer's current
  // selection (props) each time this dialog opens.
  const [selectedDate, setSelectedDate] = useState<string>(date);
  const [selectedMealtime, setSelectedMealtime] = useState<number>(mealtime);
  const [selectedPatientId, setSelectedPatientId] = useState<number | null>(
    fKey_Patient > 0 ? fKey_Patient : null
  );

  // Debounced toolbar inputs — mirror the visualizer's 2s
  // buffered-refresh pattern (TrayTicketVisualizerDialog.tsx:148-174).
  // Every query that keys off date / mealtime / patient reads
  // the DEBOUNCED scalars so a rapid string of user clicks
  // doesn't spam the server. Timer restarts on each raw-input
  // change; UI stays showing the previously-committed values
  // until 2000ms of quiet has passed. Timer is SUSPENDED while
  // either popup (Patient Select / date calendar) is open — nothing
  // commits until the user picks + closes AND 2000ms of quiet.
  const [debouncedDate, setDebouncedDate] = useState<string>(selectedDate);
  const [debouncedMealtime, setDebouncedMealtime] = useState<number>(selectedMealtime);
  const [debouncedPatientId, setDebouncedPatientId] = useState<number | null>(selectedPatientId);
  const [patientSelectOpen, setPatientSelectOpen] = useState(false);
  const [dateEditOpen, setDateEditOpen] = useState(false);

  useEffect(() => {
    if (patientSelectOpen || dateEditOpen) return;
    const t = setTimeout(() => {
      setDebouncedDate(selectedDate);
      setDebouncedMealtime(selectedMealtime);
      setDebouncedPatientId(selectedPatientId);
    }, 2000);
    return () => clearTimeout(t);
  }, [selectedDate, selectedMealtime, selectedPatientId, patientSelectOpen, dateEditOpen]);

  // Reseed on open — mirrors the parent visualizer's reseed effect.
  useEffect(() => {
    if (!open) return;
    setSelectedDate(date);
    // Explainer only supports main mealtimes (0/1/2). Nourishment
    // slots (3-8) from the visualizer collapse to their meal per
    // WinForms `Show(fKey_Facility, mealtime, d, fKey_Patient)`
    // (.vb:117-128) — 3/4→0, 5→1, 6→2.
    let mt = mealtime;
    if (mt === 3 || mt === 4) mt = 0;
    else if (mt === 5) mt = 1;
    else if (mt === 6) mt = 2;
    if (mt < 0 || mt > 2) mt = 0;
    setSelectedMealtime(mt);
    setSelectedPatientId(fKey_Patient > 0 ? fKey_Patient : null);
  }, [open, date, mealtime, fKey_Patient]);

  // Patient list + menu-cycle queries — same endpoints the
  // visualizer uses. Enabled only while the dialog is open.
  // Key off DEBOUNCED scalars per the 2s buffered-refresh rule.
  const patientListQuery = useTrayTicketPatientList(facilityId, debouncedDate, debouncedMealtime, open);
  const menuCycleQuery = useTrayTicketMenuCycle(facilityId, debouncedDate, open);

  const patients = patientListQuery.data ?? [];

  // Guest-then-host ordering matches the visualizer's
  // `sortedPatients`: hosts first (by name), each host followed
  // by its guests (indented by prefix). Orphan guests appended.
  const sortedPatients = useMemo(() => {
    const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
    const isG = (p: typeof patients[number]) => p.host_FKey_Patient != null;
    const hosts = patients.filter((p) => !isG(p));
    const guestsByHost = new Map<number, typeof patients>();
    for (const g of patients) {
      if (!isG(g) || g.host_FKey_Patient == null) continue;
      const arr = guestsByHost.get(g.host_FKey_Patient) ?? [];
      arr.push(g);
      guestsByHost.set(g.host_FKey_Patient, arr);
    }
    hosts.sort((a, b) => collator.compare(a.fullName, b.fullName));
    for (const arr of guestsByHost.values()) {
      arr.sort((a, b) => collator.compare(a.fullName, b.fullName));
    }
    const out: typeof patients = [];
    for (const h of hosts) {
      out.push(h);
      const gs = guestsByHost.get(h.fKey_Patient);
      if (gs) out.push(...gs);
    }
    for (const g of patients) {
      if (!isG(g)) continue;
      if (out.includes(g)) continue;
      out.push(g);
    }
    return out;
  }, [patients]);

  // ─── Explainer core — WinForms `ExecuteMenuRegeneration` port ───
  //
  // Initial-load call (`useTrayTicketExplainer`) runs the 3-pass
  // generator server-side and returns:
  //   • `filteredMenu` — Pass 3 with EMPTY include lists (patient
  //     diet order only, no MPs / Adjs applied). Rendered in the
  //     "Tray Ticket" pane.
  //   • `houseMenu` — Pass 1 baseline (House diet only).
  //   • `stepTree` — flat list of MPs / Adjs that WOULD execute
  //     (Pass 2 capture). Rendered as a checkbox tree.
  //   • Diet order text / cycle info / not-on-menu flag.
  //
  // User checks a step → local include-list state updates →
  // `regenerateMutation` fires (server Pass 3 only) → returned
  // `filteredMenu` replaces what the grid shows. The step tree
  // itself is NOT re-issued on regenerate (server sends empty
  // lists back for stepTree / houseMenu — client keeps its
  // initial versions).
  // Key off DEBOUNCED scalars — rapid toolbar changes shouldn't
  // fire the expensive 3-pass generator. UI stays on the last
  // committed request until 2s of quiet has passed.
  const explainerRequest = useMemo((): ExplainerRequest | null => {
    if (!open) return null;
    if (!debouncedPatientId || !debouncedDate) return null;
    return {
      date: debouncedDate,
      mealtime: debouncedMealtime,
      fKey_Patient: debouncedPatientId,
    };
  }, [open, debouncedDate, debouncedMealtime, debouncedPatientId]);

  const explainerQuery = useTrayTicketExplainer(facilityId, explainerRequest);
  const regenerateMutation = useRegenerateExplainer(facilityId);

  // Local include-list state — mirrors WinForms
  // `lstFKeyAdjustmentInclude` / `lstFKeyMealPatternInclude` /
  // `…GlobalInclude` (`.vb:858-861`). Reset on any request-key
  // change (new patient / date / mealtime → 3-pass restart).
  const [adjIncl, setAdjIncl] = useState<number[]>([]);
  const [adjGlobalIncl, setAdjGlobalIncl] = useState<number[]>([]);
  const [mpIncl, setMpIncl] = useState<number[]>([]);
  const [mpGlobalIncl, setMpGlobalIncl] = useState<number[]>([]);
  // "Diet Order" child checkbox — WinForms `UseResidentDT()`
  // (.vb:1412-1420) checks whether the diet-order CHILD node
  // (`pkey_DietOrder = 1000`) is checked. Off → MP/Adj
  // checkboxes disabled + no markers. On → MP/Adj enabled,
  // per-item markers rendered. Default TRUE per WinForms
  // initial state (`.vb:945` `SetCheckedNode(...Checked)`).
  const [dietOrderChecked, setDietOrderChecked] = useState<boolean>(true);

  // Focused menu-item row — drives the WinForms
  // `lstNodesActive` navy tint (frmDietech_Prd_TrayTicket_Explain.vb:412).
  // When the user clicks a row in the Tray Ticket grid, every
  // step whose `affectsMenuItems` includes this PKey (AND is
  // currently in the applicable include list) gets tinted navy
  // in the step tree. Null → no tinting.
  const [focusedMenuItemPKey, setFocusedMenuItemPKey] = useState<number | null>(null);

  // Snapshot of the include lists as of the last successful
  // regenerate — anchors the "(N)" pending-changes counter in
  // the toolbar (WinForms `mTxtChangesCount.Caption` +
  // `lstNodesChangesPrevious` .vb:987-994). Counter = symmetric
  // diff between the CURRENT include sets and this snapshot.
  const [lastCommittedIncludes, setLastCommittedIncludes] = useState<{
    adj: Set<number>; adjGlobal: Set<number>;
    mp: Set<number>; mpGlobal: Set<number>;
  }>(() => ({ adj: new Set(), adjGlobal: new Set(), mp: new Set(), mpGlobal: new Set() }));

  useEffect(() => {
    setAdjIncl([]);
    setAdjGlobalIncl([]);
    setMpIncl([]);
    setMpGlobalIncl([]);
    setDietOrderChecked(true);
    setFocusedMenuItemPKey(null);
    setLastCommittedIncludes({ adj: new Set(), adjGlobal: new Set(), mp: new Set(), mpGlobal: new Set() });
    // Cancel any pending tree regenerate — otherwise a stale
    // debounce would fire against the new patient / date /
    // mealtime with the old include-list values.
    cancelTreeDebounce();
  }, [selectedDate, selectedMealtime, selectedPatientId]);

  // Full clear on close — wipe every local state field back to
  // safe defaults AND remove the explainer-specific query cache
  // entries. Everything the Explainer touched gets dropped so
  // the next open starts from a clean slate; the on-open reseed
  // effect then re-populates from the current props. Only
  // invalidates the `trayTicket / explainer` sub-key — the
  // parent visualizer is still open above and its `menuCycle` /
  // `patientList` / `data` caches must not be blown away.
  const queryClient = useQueryClient();
  useEffect(() => {
    if (open) return;
    setSelectedDate(todayStr());
    setSelectedMealtime(0);
    setSelectedPatientId(null);
    setAdjIncl([]);
    setAdjGlobalIncl([]);
    setMpIncl([]);
    setMpGlobalIncl([]);
    setDietOrderChecked(true);
    setFocusedMenuItemPKey(null);
    setLastCommittedIncludes({ adj: new Set(), adjGlobal: new Set(), mp: new Set(), mpGlobal: new Set() });
    setRegenMenu(null);
    setRegenItemDetails({});
    cancelTreeDebounce();
    queryClient.removeQueries({ queryKey: [...trayTicketKeys.all, 'explainer'] });
  }, [open, queryClient]);

  // Overlay the server's regenerate response onto the initial
  // explainer data — regenerate only refreshes `filteredMenu`;
  // step tree + house menu + diet info stay from the initial
  // GenerateExplainer call.
  const [regenMenu, setRegenMenu] = useState<ExplainerMenuItem[] | null>(null);
  // Accumulator for the item-detail map returned by each
  // successive regenerate call. Merged on top of the initial
  // `explainerData.itemDetails` in `focusedItemDetails` so any
  // item that appears in a regenerated menu — even ones NOT in
  // the initial filteredMenu / houseMenu union — has its food-
  // group / allergy pane populated. Reset whenever the request
  // key changes (patient / date / mealtime) so stale entries
  // for other patients never leak.
  const [regenItemDetails, setRegenItemDetails] = useState<Record<number, ExplainerResult['itemDetails'][number]>>({});
  useEffect(() => {
    // Fresh initial load or request change → drop any stale
    // regenerate overlay so the grid falls back to the query's
    // fresh `filteredMenu`.
    setRegenMenu(null);
    setRegenItemDetails({});
  }, [explainerRequest]);

  const explainerData: ExplainerResult | undefined = explainerQuery.data;
  const filteredMenu: ExplainerMenuItem[] = regenMenu ?? explainerData?.filteredMenu ?? [];
  const stepTree: ExplainerStepNode[] = explainerData?.stepTree ?? [];
  // Grid always shows `filteredMenu` (Pass 3) — the user's spec:
  // "initial menu items should only be using the current patient
  // diet order and not include any meal pattern or adjustment
  // items". Pass 3 with EMPTY include lists produces exactly
  // that. The Diet Order checkbox only controls whether per-
  // item markers render (via `dietOrderChecked` gate below).
  const displayedMenu = filteredMenu;

  // Facility-setting abbreviations for the marker text
  // (MealPatternShortDescription / AdjustmentsShortDescription).
  const mpAbbr = useMemo(
    () => generateAbbreviation((facilitySettings as unknown as Record<string, string> | null)?.mealPatternShortDescription),
    [facilitySettings],
  );
  const adjAbbr = useMemo(
    () => generateAbbreviation((facilitySettings as unknown as Record<string, string> | null)?.adjustmentsShortDescription),
    [facilitySettings],
  );

  // Reverse index: PKey_Prd_Menu_Detail → { text, kind }.
  // `kind` drives the pill color (distinct per marker type).
  // Priority order + gate mirror WinForms `gv_CustomDrawCell`
  // (.vb:322-410) — first match wins.
  const markerByPKey = useMemo(() => {
    const map = new Map<number, ExplainerMarker>();
    if (!dietOrderChecked) return map;
    const mpSet = new Set(mpIncl);
    const mpGlobalSet = new Set(mpGlobalIncl);
    const adjSet = new Set(adjIncl);
    const adjGlobalSet = new Set(adjGlobalIncl);
    for (const node of stepTree) {
      if (node.fKey_Adj_MP == null || node.affectsMenuItems.length === 0) continue;
      const id = node.fKey_Adj_MP;
      let marker: ExplainerMarker | null = null;
      if (node.stepType === 2 && !node.isGlobal && mpSet.has(id)) {
        marker = { text: mpAbbr, kind: 'mp' };
      } else if (node.stepType === 2 && node.isGlobal && mpGlobalSet.has(id)) {
        marker = { text: `${mpAbbr} G`, kind: 'mp-global' };
      } else if (node.stepType >= 4 && node.stepType <= 14 && !node.isGlobal && adjSet.has(id)) {
        marker = { text: adjAbbr, kind: 'adj' };
      } else if (node.stepType >= 4 && node.stepType <= 14 && node.isGlobal && adjGlobalSet.has(id)) {
        marker = { text: `${adjAbbr} G`, kind: 'adj-global' };
      }
      if (!marker) continue;
      for (const pkey of node.affectsMenuItems) {
        if (!map.has(pkey)) map.set(pkey, marker);
      }
    }
    // AlwaysServed applies last (lowest priority). Populated
    // from the filtered menu since AS items only surface once
    // Pass 3 has run.
    for (const item of filteredMenu) {
      if (item.isAlwaysServed && !map.has(item.pKey_Prd_Menu_Detail)) {
        map.set(item.pKey_Prd_Menu_Detail, { text: 'AS', kind: 'as' });
      }
    }
    return map;
  }, [dietOrderChecked, stepTree, mpIncl, mpGlobalIncl, adjIncl, adjGlobalIncl, mpAbbr, adjAbbr, filteredMenu]);

  // Toggle a step's checkbox. Adjustment steps use `stepType`
  // 4-14 (Modify / Next / NeverServe / Replace / …); meal
  // pattern step is `stepType == 2`. Global flag routes into
  // the *_Global_* list. After the local list flips, fire
  // RegenerateExplainer and stash the new `filteredMenu` in
  // `regenMenu`.
  const stepCheckState = useMemo(() => {
    const s = new Set<number>();
    adjIncl.forEach((id) => s.add(id));
    adjGlobalIncl.forEach((id) => s.add(id));
    mpIncl.forEach((id) => s.add(id));
    mpGlobalIncl.forEach((id) => s.add(id));
    return s;
  }, [adjIncl, adjGlobalIncl, mpIncl, mpGlobalIncl]);

  // Food-group + allergy summary for the currently-focused menu
  // row. Server pre-computed one entry per distinct FKey_Item in
  // the initial GenerateExplainer response so this is a cheap
  // in-memory lookup — no round-trip per grid click. Mirrors
  // WinForms `RefreshItemFoodGroupAndAllergies` (.vb:1425).
  //
  // Row lookup covers BOTH the currently-displayed menu
  // (`filteredMenu` state — regenMenu wins over the initial
  // response after a Refresh) AND the initial explainerData's
  // menu tables, since a row focused post-regenerate might not
  // appear in the initial payload but still needs its item's
  // details rendered. `itemDetails` is populated by
  // GenerateExplainer for every distinct FKey_Item seen in
  // filteredMenu ∪ houseMenu, so the lookup succeeds as long
  // as fKey_Item stays in that union.
  //
  // ItemDetails-map keys arrive as strings over the wire (JSON
  // objects always stringify their keys), but JavaScript
  // coerces numeric indices to strings on lookup, so
  // `map[fKey_Item]` works — no explicit coercion needed.
  const focusedItemDetails = useMemo(() => {
    if (focusedMenuItemPKey == null || !explainerData) return null;
    const item =
      filteredMenu.find((x) => x.pKey_Prd_Menu_Detail === focusedMenuItemPKey)
      ?? (explainerData.filteredMenu ?? []).find((x) => x.pKey_Prd_Menu_Detail === focusedMenuItemPKey)
      ?? (explainerData.houseMenu ?? []).find((x) => x.pKey_Prd_Menu_Detail === focusedMenuItemPKey);
    if (!item || item.fKey_Item <= 0) return null;
    // Regenerate accumulator wins over the initial map — later
    // requests may have refreshed an item's food-group or
    // allergy assignments and we want the newest data.
    return regenItemDetails[item.fKey_Item]
      ?? explainerData.itemDetails?.[item.fKey_Item]
      ?? null;
  }, [focusedMenuItemPKey, filteredMenu, explainerData, regenItemDetails]);

  // Active-node set — WinForms `lstNodesActive` (.vb:412-448).
  // Recomputed whenever the focused menu-item row changes; a
  // step qualifies only if it (a) actually affected the focused
  // item AND (b) is currently in the applicable include list.
  const activeNodeIds = useMemo(() => {
    const s = new Set<number>();
    if (focusedMenuItemPKey == null) return s;
    const mpSet = new Set(mpIncl);
    const mpGlobalSet = new Set(mpGlobalIncl);
    const adjSet = new Set(adjIncl);
    const adjGlobalSet = new Set(adjGlobalIncl);
    for (const node of stepTree) {
      if (node.fKey_Adj_MP == null) continue;
      if (!node.affectsMenuItems.includes(focusedMenuItemPKey)) continue;
      const id = node.fKey_Adj_MP;
      let included = false;
      if (node.stepType === 2 && !node.isGlobal) included = mpSet.has(id);
      else if (node.stepType === 2 && node.isGlobal) included = mpGlobalSet.has(id);
      else if (node.stepType >= 4 && node.stepType <= 14 && !node.isGlobal) included = adjSet.has(id);
      else if (node.stepType >= 4 && node.stepType <= 14 && node.isGlobal) included = adjGlobalSet.has(id);
      if (included) s.add(node.id);
    }
    return s;
  }, [focusedMenuItemPKey, stepTree, mpIncl, mpGlobalIncl, adjIncl, adjGlobalIncl]);

  // WinForms `UseResidentDT()` (.vb:1412-1420) — true iff the
  // Diet Order child (pkey_DietOrder = 1000) is checked. When
  // false, `SetMPAdjDisabled()` (.vb:1395) grays out every MP/
  // Adj node (roots + descendants). Purely derived, so no state.
  const mpAdjDisabled = !dietOrderChecked;

  // Pending-changes counter — symmetric diff between each
  // current include list and its last-committed snapshot.
  // Renders as "(N)" in the toolbar next to Refresh; blank when
  // zero. Mirrors WinForms `mTxtChangesCount.Caption` computed
  // from `lstNodesChanges` (.vb:700-720).
  const changesCount = useMemo(() => {
    const diff = (curr: number[], snap: Set<number>) => {
      const currSet = new Set(curr);
      let n = 0;
      for (const id of curr) if (!snap.has(id)) n++;
      for (const id of snap) if (!currSet.has(id)) n++;
      return n;
    };
    return diff(adjIncl, lastCommittedIncludes.adj)
      + diff(adjGlobalIncl, lastCommittedIncludes.adjGlobal)
      + diff(mpIncl, lastCommittedIncludes.mp)
      + diff(mpGlobalIncl, lastCommittedIncludes.mpGlobal);
  }, [adjIncl, adjGlobalIncl, mpIncl, mpGlobalIncl, lastCommittedIncludes]);

  // 2s debounce ref for tree-driven regenerate. Every step-tree
  // checkbox click resets this timer; when it fires, the current
  // include-list state (read via refs to avoid stale closures)
  // gets sent to the server. Cancelled on request-key change
  // and on unmount / dialog close so pending regenerate calls
  // don't hit against a stale patient / date / mealtime.
  const treeDebounceRef = useRef<number | null>(null);
  const cancelTreeDebounce = () => {
    if (treeDebounceRef.current != null) {
      window.clearTimeout(treeDebounceRef.current);
      treeDebounceRef.current = null;
    }
  };

  // Live refs — the debounce callback fires after state has
  // possibly changed again; reading refs guarantees we send the
  // latest values, not the snapshot from the closure that
  // scheduled the timer.
  const explainerRequestRef = useRef(explainerRequest);
  const adjInclRef = useRef(adjIncl);
  const adjGlobalInclRef = useRef(adjGlobalIncl);
  const mpInclRef = useRef(mpIncl);
  const mpGlobalInclRef = useRef(mpGlobalIncl);
  useEffect(() => { explainerRequestRef.current = explainerRequest; }, [explainerRequest]);
  useEffect(() => { adjInclRef.current = adjIncl; }, [adjIncl]);
  useEffect(() => { adjGlobalInclRef.current = adjGlobalIncl; }, [adjGlobalIncl]);
  useEffect(() => { mpInclRef.current = mpIncl; }, [mpIncl]);
  useEffect(() => { mpGlobalInclRef.current = mpGlobalIncl; }, [mpGlobalIncl]);

  // Fire the actual regenerate call and commit the snapshot on
  // success. Reads the LIVE include-list state via refs so a
  // debounced call always sends the latest values. Callers that
  // want the request-key from a specific-moment closure (e.g.
  // the Refresh button) can pass an explicit request; otherwise
  // we read the current one from the ref.
  const runRegenerate = async () => {
    const req = explainerRequestRef.current;
    if (!req) return;
    const nextAdj = adjInclRef.current;
    const nextAdjGlobal = adjGlobalInclRef.current;
    const nextMp = mpInclRef.current;
    const nextMpGlobal = mpGlobalInclRef.current;
    const regenRequest: ExplainerRegenerateRequest = {
      ...req,
      adjustmentInclude: nextAdj,
      adjustmentGlobalInclude: nextAdjGlobal,
      mealPatternInclude: nextMp,
      mealPatternGlobalInclude: nextMpGlobal,
      useDietOrder: true,
    };
    try {
      const result = await regenerateMutation.mutateAsync(regenRequest);
      setRegenMenu(result.filteredMenu ?? []);
      // Merge the server's regenerate itemDetails INTO the
      // accumulator — the initial GenerateExplainer call only
      // pre-populated the union of the initial filteredMenu +
      // houseMenu. A regenerated menu can surface NEW items
      // (an adjustment that adds a substitution) whose FKey_Item
      // wasn't in that initial union; without this merge the
      // pane would render "No food-group / allergy data
      // available" for those rows. Keys arrive over the wire as
      // strings and are coerced to numbers on lookup elsewhere,
      // so the spread merge is safe.
      if (result.itemDetails) {
        setRegenItemDetails((prev) => ({ ...prev, ...result.itemDetails }));
      }
      // Commit snapshot — mirrors WinForms
      // `lstNodesChangesPrevious` rebuild after regenerate
      // (.vb:987-994). Any subsequent leaf toggle before the
      // next regenerate re-populates `changesCount`.
      setLastCommittedIncludes({
        adj: new Set(nextAdj),
        adjGlobal: new Set(nextAdjGlobal),
        mp: new Set(nextMp),
        mpGlobal: new Set(nextMpGlobal),
      });
      // New generation → drop the focused row so the food-group
      // / allergy pane clears. Grid rows may have shifted, and
      // an old PKey_Prd_Menu_Detail could resolve to a different
      // item post-regenerate.
      setFocusedMenuItemPKey(null);
    } catch {
      /* keep previous grid; toggles stay reflected */
    }
  };

  // Stage include-list changes AND schedule a debounced
  // regenerate 2s later. UI (grid marker gates, tree check
  // states, (N) counter) all update immediately from the local
  // state; only the server round-trip is deferred. Every fresh
  // call resets the timer so a burst of checkbox clicks fires
  // ONE regenerate at the end.
  const applyIncludeLists = (
    nextAdj: number[], nextAdjGlobal: number[],
    nextMp: number[], nextMpGlobal: number[],
  ) => {
    setAdjIncl(nextAdj);
    setAdjGlobalIncl(nextAdjGlobal);
    setMpIncl(nextMp);
    setMpGlobalIncl(nextMpGlobal);
    // Keep refs synchronous with the values we just staged so
    // an immediate refresh (which reads the refs) sees them
    // before React commits. State setters above still queue
    // the render for downstream consumers.
    adjInclRef.current = nextAdj;
    adjGlobalInclRef.current = nextAdjGlobal;
    mpInclRef.current = nextMp;
    mpGlobalInclRef.current = nextMpGlobal;
    cancelTreeDebounce();
    treeDebounceRef.current = window.setTimeout(() => {
      treeDebounceRef.current = null;
      void runRegenerate();
    }, 2000);
  };

  const handleStepToggle = (node: ExplainerStepNode) => {
    const stepTreeData = explainerData?.stepTree ?? [];

    // Diet Order ROOT (id=1) OR Diet Order CHILD (stepType=1,
    // parentId=1) — WinForms `trl_StateImageClick .vb:676` handles
    // BOTH cases identically. Toggling either flips the marker /
    // MP-Adj-enable gate.
    if (node.id === 1 || (node.stepType === 1 && node.parentId === 1)) {
      setDietOrderChecked((v) => !v);
      return;
    }

    // Meal Pattern ROOT (id=2) — toggle-all. If ANY MP leaf is
    // included, unchecking clears the entire MP include set;
    // otherwise check every MP leaf.
    if (node.id === 2) {
      const mpLeaves = stepTreeData.filter((s) => s.stepType === 2 && s.fKey_Adj_MP != null);
      const anyChecked = mpLeaves.some((s) =>
        s.isGlobal
          ? mpGlobalIncl.includes(s.fKey_Adj_MP!)
          : mpIncl.includes(s.fKey_Adj_MP!)
      );
      if (anyChecked) {
        applyIncludeLists(adjIncl, adjGlobalIncl, [], []);
      } else {
        const nextMp: number[] = [];
        const nextMpGlobal: number[] = [];
        for (const l of mpLeaves) {
          if (l.isGlobal) nextMpGlobal.push(l.fKey_Adj_MP!);
          else nextMp.push(l.fKey_Adj_MP!);
        }
        applyIncludeLists(adjIncl, adjGlobalIncl, nextMp, nextMpGlobal);
      }
      return;
    }

    // Adjustments ROOT (id=3) — same toggle-all semantics for Adj.
    if (node.id === 3) {
      const adjLeaves = stepTreeData.filter((s) => s.stepType >= 4 && s.stepType <= 14 && s.fKey_Adj_MP != null);
      const anyChecked = adjLeaves.some((s) =>
        s.isGlobal
          ? adjGlobalIncl.includes(s.fKey_Adj_MP!)
          : adjIncl.includes(s.fKey_Adj_MP!)
      );
      if (anyChecked) {
        applyIncludeLists([], [], mpIncl, mpGlobalIncl);
      } else {
        const nextAdj: number[] = [];
        const nextAdjGlobal: number[] = [];
        for (const l of adjLeaves) {
          if (l.isGlobal) nextAdjGlobal.push(l.fKey_Adj_MP!);
          else nextAdj.push(l.fKey_Adj_MP!);
        }
        applyIncludeLists(nextAdj, nextAdjGlobal, mpIncl, mpGlobalIncl);
      }
      return;
    }

    // Adj sub-header (Diet Override / Modify / etc.) — its
    // `fKey_Adj_MP` is null and `stepType` is 4-14. Toggle-all
    // its adjustment children within the include lists.
    if (node.stepType >= 4 && node.stepType <= 14 && node.fKey_Adj_MP == null) {
      const subLeaves = stepTreeData.filter(
        (s) => s.parentId === node.id && s.fKey_Adj_MP != null,
      );
      const anyChecked = subLeaves.some((s) =>
        s.isGlobal
          ? adjGlobalIncl.includes(s.fKey_Adj_MP!)
          : adjIncl.includes(s.fKey_Adj_MP!)
      );
      const subGlobalIds = subLeaves.filter((s) => s.isGlobal).map((s) => s.fKey_Adj_MP!);
      const subLocalIds  = subLeaves.filter((s) => !s.isGlobal).map((s) => s.fKey_Adj_MP!);
      let nextAdj = adjIncl.filter((id) => !subLocalIds.includes(id));
      let nextAdjGlobal = adjGlobalIncl.filter((id) => !subGlobalIds.includes(id));
      if (!anyChecked) {
        nextAdj = [...nextAdj, ...subLocalIds];
        nextAdjGlobal = [...nextAdjGlobal, ...subGlobalIds];
      }
      applyIncludeLists(nextAdj, nextAdjGlobal, mpIncl, mpGlobalIncl);
      return;
    }

    // Individual MP / Adj leaf — flip its include-list membership.
    if (node.fKey_Adj_MP == null) return;
    const id = node.fKey_Adj_MP;
    const isAdj = node.stepType >= 4 && node.stepType <= 14;
    const isMP = node.stepType === 2;
    if (!isAdj && !isMP) return;

    let nextAdj = adjIncl;
    let nextAdjGlobal = adjGlobalIncl;
    let nextMp = mpIncl;
    let nextMpGlobal = mpGlobalIncl;
    const toggle = (arr: number[]) =>
      arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];
    if (isAdj && node.isGlobal)      nextAdjGlobal = toggle(adjGlobalIncl);
    else if (isAdj)                  nextAdj       = toggle(adjIncl);
    else if (isMP && node.isGlobal)  nextMpGlobal  = toggle(mpGlobalIncl);
    else if (isMP)                   nextMp        = toggle(mpIncl);

    applyIncludeLists(nextAdj, nextAdjGlobal, nextMp, nextMpGlobal);
  };

  const handleRefresh = () => {
    // Flush both debounces. Toolbar side: commit the raw toolbar
    // scalars into their debounced siblings so the query keys
    // update immediately (skipping the 2s window). Tree side:
    // cancel the pending 2s timer and fire the regenerate now
    // with the current staged include lists. Same intent as
    // WinForms `mbtnRefresh_ItemClick` — user asked, run now.
    setDebouncedDate(selectedDate);
    setDebouncedMealtime(selectedMealtime);
    setDebouncedPatientId(selectedPatientId);
    patientListQuery.refetch();
    menuCycleQuery.refetch();
    explainerQuery.refetch();
    cancelTreeDebounce();
    void runRegenerate();
    // Reset the focused row too — the grid rebuilds and the
    // food-group / allergy pane should start blank on refresh.
    setFocusedMenuItemPKey(null);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: { width: '80vw', maxWidth: 1020, height: '78vh', m: 1 } }}
    >
      <DialogTitle sx={{
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        py: 1, px: 2,
        fontSize: 14,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}>
        {/* Hamburger — narrow viewports only. Opens the toolbar
            drawer. Sits to the LEFT of the title so it matches
            the visualizer's mobile affordance. */}
        {isNarrow && (
          <IconButton
            size="small"
            onClick={() => setMobileDrawerOpen(true)}
            aria-label="Open toolbar"
            sx={{ color: 'inherit', padding: '4px' }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>
        )}
        <Typography sx={{ flex: 1, fontSize: 14 }}>
          Tray Ticket Explanation {patientName ?? ''}...
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close"
          sx={{ color: 'primary.contrastText' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Toolbar — mirrors WinForms `frmDietech_Prd_TrayTicket_Explain`
            top bar (Patient · Mealtime · MenuDate · MenuCycle info ·
            Refresh). Values seeded from the visualizer's current
            selection each time the dialog opens. */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.default',
        }}>
          {/* Selector cluster — hidden on narrow viewports; the
              same controls render inside the hamburger Drawer
              below. `display: contents` keeps the outer toolbar's
              flex-gap wrapping the visible children so Refresh
              still sits flush after the last visible control. */}
          <Box sx={{ display: isNarrow ? 'none' : 'contents' }}>
          {/* Patient */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select<number | ''>
              value={selectedPatientId ?? ''}
              onChange={(e) => setSelectedPatientId(Number(e.target.value) || null)}
              onOpen={() => setPatientSelectOpen(true)}
              onClose={() => setPatientSelectOpen(false)}
              displayEmpty
              IconComponent={patientListQuery.isLoading
                ? (() => (
                  <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 24, height: 24, position: 'absolute', right: 4, top: '50%',
                    transform: 'translateY(-50%)', pointerEvents: 'none',
                  }}>
                    <CircularProgress size={12} thickness={5} />
                  </Box>
                ))
                : undefined}
              renderValue={(v) => {
                if (v === '' || v == null) {
                  return <em style={{ color: 'var(--text-secondary)' }}>
                    {patientListQuery.isLoading ? 'Loading...' : 'Select resident'}
                  </em>;
                }
                const p = sortedPatients.find((x) => x.fKey_Patient === v);
                return <span style={{ fontSize: 14 }}>{p?.fullName ?? ''}</span>;
              }}
              sx={{ height: 30, fontSize: 14, bgcolor: 'common.white' }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 400, minWidth: 260 } } }}
            >
              <MenuItem value="" disabled sx={{ display: 'none' }} />
              {sortedPatients.map((p) => {
                const guest = p.host_FKey_Patient != null;
                return (
                  <MenuItem key={p.fKey_Patient} value={p.fKey_Patient} sx={{ fontSize: 14, py: 0.5 }}>
                    {guest && <span style={{ marginRight: 6 }}>↳</span>}
                    {p.fullName}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {/* Mealtime — main meals only (WinForms `GetMainMealNames`) */}
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select
              value={selectedMealtime}
              onChange={(e) => setSelectedMealtime(Number(e.target.value))}
              sx={{ height: 30, fontSize: 14, bgcolor: 'common.white' }}
            >
              {[0, 1, 2].map((slot) => {
                const name = (facilityMealNames[slot] ?? '').trim() || MEALTIME_DEFAULTS[slot];
                return (
                  <MenuItem key={slot} value={slot} sx={{ fontSize: 14 }}>
                    {name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {/* Date — same layout as the visualizer toolbar
              (chevron / date / chevron). The server re-resolves
              the cycle for whatever date is picked. */}
          <DateField
            ariaLabel="Date"
            value={selectedDate}
            onValueChanged={(v) => { if (v) setSelectedDate(v); }}
            onOpened={() => setDateEditOpen(true)}
            onClosed={() => setDateEditOpen(false)}
            displayFormat="M/D/YYYY"
            width={194}
            height={34}
            showStepButtons
          />

          {/* Menu cycle info — matches WinForms
              `GetMenuCycleDisplayString(...)` shown next to the
              date picker (e.g. `Tue, Week 1, Day 3 | MC Day 3`).
              We render the DOW inline with `MC Day X` since the
              lightweight endpoint doesn't return Start_Date /
              NumberOfDaysInCycle yet. */}
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 160 }}>
            {menuCycleQuery.data?.menuCycleName ? (
              <>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.3 }}>
                  {menuCycleQuery.data.menuCycleName}
                </Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.3 }}>
                  {new Date(`${selectedDate}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short' })}
                  {menuCycleQuery.data.menuDay > 0 && <> {' | '} MC Day {menuCycleQuery.data.menuDay}</>}
                </Typography>
              </>
            ) : (
              !menuCycleQuery.isLoading && menuCycleQuery.data && (
                <Typography sx={{ fontSize: 11, fontStyle: 'italic', color: 'text.disabled' }}>
                  No active menu cycle
                </Typography>
              )
            )}
          </Box>

          {/* Vertical divider before Refresh — separates the
              selector cluster (patient / mealtime / date / cycle
              info) from the action cluster. Only meaningful when
              the selector cluster is visible. */}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
          </Box>

          {/* Refresh — proper outlined toolbar button (mirrors
              the visualizer mobile summary's action buttons).
              Fires WinForms `mbtnRefresh_ItemClick` equivalent. */}
          <Tooltip title="Refresh">
            <span>
              <Button
                size="small"
                variant="outlined"
                onClick={handleRefresh}
                aria-label="Refresh"
                startIcon={<i className="bi bi-arrow-clockwise" style={{ fontSize: 14 }} />}
                sx={{ py: 0.5, textTransform: 'none' }}
              >
                Refresh
              </Button>
            </span>
          </Tooltip>

          {/* Pending-changes counter — WinForms
              `mTxtChangesCount.Caption` (.vb:716-720). Sits to
              the RIGHT of Refresh so the user sees how many
              un-committed leaf toggles are pending. Hidden when
              zero. Always renders regardless of narrow / wide
              layout — the selector cluster hides on mobile but
              the counter stays with Refresh. */}
          {changesCount > 0 && (
            <Tooltip title="Uncommitted step toggles. Click Refresh to apply.">
              <Box sx={{
                px: 0.75, py: 0.25,
                borderRadius: '10px',
                bgcolor: '#f57c00',
                color: 'common.white',
                fontSize: 12, fontWeight: 700, lineHeight: '1.4',
                minWidth: 24, textAlign: 'center',
                cursor: 'default',
              }}>
                ({changesCount})
              </Box>
            </Tooltip>
          )}
        </Box>

        {/* Body — 3 resizable sections. Layout mirrors WinForms
            `SplitContainerControl1` + `SplitContainerControl2`
            (.Designer.vb:98-125):
              • Left column (nested vertical split):
                  - Top 70% → menu items grid
                  - Bottom 30% → food-group / allergy scroller
              • Right column (single pane) → step tree
            Handles resize in every direction; localStorage keys
            (`autoSaveId`) persist user-adjusted sizes. */}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
          <PanelGroup orientation="horizontal" style={{ width: '100%', height: '100%' }}>
            <Panel defaultSize={60} minSize={25}>
              <PanelGroup orientation="vertical" style={{ width: '100%', height: '100%' }}>
                <Panel defaultSize={70} minSize={20}>
                  <div className="tte-section">
                    {/* Left top — menu items (WinForms `dgr`/`gv`
                        grid). Renders Pass 3 output (or the last
                        RegenerateExplainer response). */}
                    <div className="tte-section-header">Tray Ticket</div>
                    <div className="tte-section-body">
                      {/* Shimmer during any fetch or regenerate,
                          OR when there's no data yet — matches
                          the visualizer's `<MenuItemsShimmer>`
                          pattern. No spinner in the header and
                          no "Loading…" text. */}
                      {explainerQuery.isFetching || regenerateMutation.isPending || !explainerData ? (
                        <MenuItemsShimmer />
                      ) : displayedMenu.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No menu items for the current selection.
                        </Typography>
                      ) : (
                        <Box sx={{ fontFamily: '"Times New Roman", Times, serif', fontSize: 15, lineHeight: 1.4 }}>
                          {displayedMenu.map((item, i) => (
                            <ExplainerItemRow
                              key={`${item.pKey_Prd_Menu_Detail}-${i}`}
                              item={item}
                              marker={markerByPKey.get(item.pKey_Prd_Menu_Detail) ?? null}
                              isFocused={item.pKey_Prd_Menu_Detail === focusedMenuItemPKey}
                              onFocus={() => setFocusedMenuItemPKey(item.pKey_Prd_Menu_Detail)}
                              useCaps={useCaps}
                              useColor={useColor}
                            />
                          ))}
                        </Box>
                      )}
                    </div>
                  </div>
                </Panel>
                <PanelResizeHandle className="tte-handle-horizontal" />
                <Panel defaultSize={30} minSize={10}>
                  <div className="tte-section">
                    {/* Left bottom — food-group / allergy scroller
                        (WinForms `scrollerFoodGroupAllergy`,
                        rebuilt per focused row by
                        `RefreshItemFoodGroupAndAllergies`
                        .vb:1425). No header per spec. Empty state
                        when nothing is focused — matches
                        WinForms `If row Is Nothing Then Return`. */}
                    <div className="tte-section-body">
                      <ExplainerFoodGroupAllergy
                        details={focusedItemDetails}
                        hasFocus={focusedMenuItemPKey != null}
                      />
                    </div>
                  </div>
                </Panel>
              </PanelGroup>
            </Panel>
            <PanelResizeHandle className="tte-handle-vertical" />
            <Panel defaultSize={40} minSize={20}>
              <div className="tte-section">
                {/* Right — step tree (WinForms `trl` TreeList).
                    Nodes come from Pass 2 (`stepTree`); checking
                    one adds its FKey to the appropriate include
                    list and re-runs Pass 3 via RegenerateExplainer. */}
                <div className="tte-section-header">Steps</div>
                <div className="tte-section-body">
                  {explainerQuery.isFetching || !explainerData ? (
                    <MenuItemsShimmer />
                  ) : stepTree.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No steps applicable.
                    </Typography>
                  ) : (
                    <ExplainerStepList
                      nodes={stepTree}
                      checked={stepCheckState}
                      dietOrderChecked={dietOrderChecked}
                      disabled={regenerateMutation.isPending}
                      onToggle={handleStepToggle}
                      activeNodeIds={activeNodeIds}
                      mpAdjDisabled={mpAdjDisabled}
                    />
                  )}
                </div>
              </div>
            </Panel>
          </PanelGroup>
        </Box>
      </DialogContent>

      {/* Mobile toolbar drawer — anchor left, same pattern as the
          visualizer. Only rendered when the Explainer dialog is
          open + viewport is narrow. Contents stack vertically so
          the date / cycle info wrap cleanly at 340px. z-index
          is bumped past the Dialog so Select / calendar popups
          still float above it. `keepMounted` avoids remounting
          the date field on every open. */}
      <Drawer
        anchor="left"
        open={isNarrow && mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}
        PaperProps={{
          sx: {
            width: 320,
            maxWidth: '85vw',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          },
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.5,
        }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
            Toolbar
          </Typography>
          <IconButton
            size="small"
            aria-label="Close"
            onClick={() => setMobileDrawerOpen(false)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider />

        {/* Patient */}
        <Box>
          <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 0.5 }}>
            Resident
          </Typography>
          <FormControl size="small" fullWidth>
            <Select<number | ''>
              value={selectedPatientId ?? ''}
              onChange={(e) => setSelectedPatientId(Number(e.target.value) || null)}
              onOpen={() => setPatientSelectOpen(true)}
              onClose={() => setPatientSelectOpen(false)}
              displayEmpty
              renderValue={(v) => {
                if (v === '' || v == null) {
                  return <em style={{ color: 'var(--text-secondary)' }}>
                    {patientListQuery.isLoading ? 'Loading...' : 'Select resident'}
                  </em>;
                }
                const p = sortedPatients.find((x) => x.fKey_Patient === v);
                return <span style={{ fontSize: 14 }}>{p?.fullName ?? ''}</span>;
              }}
              sx={{ height: 34, fontSize: 14 }}
              MenuProps={{
                PaperProps: { sx: { maxHeight: 400 } },
                sx: { zIndex: (theme) => theme.zIndex.modal + 20 },
              }}
            >
              <MenuItem value="" disabled sx={{ display: 'none' }} />
              {sortedPatients.map((p) => {
                const guest = p.host_FKey_Patient != null;
                return (
                  <MenuItem key={p.fKey_Patient} value={p.fKey_Patient} sx={{ fontSize: 14, py: 0.5 }}>
                    {guest && <span style={{ marginRight: 6 }}>↳</span>}
                    {p.fullName}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>

        {/* Mealtime */}
        <Box>
          <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 0.5 }}>
            Mealtime
          </Typography>
          <FormControl size="small" fullWidth>
            <Select
              value={selectedMealtime}
              onChange={(e) => setSelectedMealtime(Number(e.target.value))}
              sx={{ height: 34, fontSize: 14 }}
              MenuProps={{ sx: { zIndex: (theme) => theme.zIndex.modal + 20 } }}
            >
              {[0, 1, 2].map((slot) => {
                const name = (facilityMealNames[slot] ?? '').trim() || MEALTIME_DEFAULTS[slot];
                return (
                  <MenuItem key={slot} value={slot} sx={{ fontSize: 14 }}>
                    {name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>

        {/* Date */}
        <Box>
          <DateField
            label="Date"
            value={selectedDate}
            onValueChanged={(v) => { if (v) setSelectedDate(v); }}
            onOpened={() => setDateEditOpen(true)}
            onClosed={() => setDateEditOpen(false)}
            displayFormat="M/D/YYYY"
            height={34}
            showStepButtons
          />
        </Box>

        {/* Menu cycle info — same content the toolbar shows. */}
        <Box>
          <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 0.5 }}>
            Menu Cycle
          </Typography>
          {menuCycleQuery.data?.menuCycleName ? (
            <>
              <Typography sx={{ fontSize: 12, lineHeight: 1.35 }}>
                {menuCycleQuery.data.menuCycleName}
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.35 }}>
                {new Date(`${selectedDate}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short' })}
                {menuCycleQuery.data.menuDay > 0 && <> {' | '} MC Day {menuCycleQuery.data.menuDay}</>}
              </Typography>
            </>
          ) : (
            !menuCycleQuery.isLoading && menuCycleQuery.data && (
              <Typography sx={{ fontSize: 12, fontStyle: 'italic', color: 'text.disabled' }}>
                No active menu cycle
              </Typography>
            )
          )}
        </Box>
      </Drawer>
    </Dialog>
  );
}

// WinForms glyph constants used by `ItemPrefix` (.vb:1319-1329)
// and `FormatItemTextWithArrow` (.vb:1264). Copied verbatim
// from the Unicode code points `PRIMEServices.Utils.Characters`
// exposes so the composed line reads identically to WinForms.
const GLYPH_INCREMENT = '∆';      // Δ — Times New Roman Increment
const GLYPH_BLACK_SQUARE = '■';   // ■ — Times New Roman Black Square
const GLYPH_ARROW = '→';          // → — Times New Roman Rightwards Arrow
const GLYPH_BLACK_CIRCLE = '●';   // ● — Times New Roman Black Circle (overridden-item "OK" suffix)

// Sentinel PKey_Prd_Portion for the "No portion" row — triggers
// portion-first ordering per WinForms
// `EnumerationsConstantsCollections.vb:13`. Matches the visualizer
// content's `PKEY_PRD_PORTION_NO` constant.
const PKEY_PRD_PORTION_NO = 15;

// DevExpress `BorderSide` bitmask — matches WinForms `RenderMenu`
// (.vb:2540-2616) and the visualizer's `ItemRow`. Each side is
// checked independently and gets its own border-side rule so
// arbitrary combinations render correctly.
const BORDER_LEFT = 1;
const BORDER_TOP = 2;
const BORDER_RIGHT = 4;
const BORDER_BOTTOM = 8;

/** WinForms `Argb_BoxColor` → CSS hex. Null-passthrough. */
function boxColorToHex(argb: number | null | undefined): string | undefined {
  if (!argb) return undefined;
  return argbToHex(argb);
}

/** WinForms `FormatItemText` (.vb:1246-1252). Non-empty → "v ". */
function formatItemText(value: string | null | undefined): string {
  const v = (value ?? '').trim();
  return v === '' ? '' : `${v} `;
}

/** WinForms `FormatItemTextWithArrow` (.vb:1256-1270). */
function formatItemTextWithArrow(
  itmDescr: string | null | undefined,
  itemType: string | null | undefined,
  hasServiceItem: boolean,
): string {
  const descr = (itmDescr ?? '').trim();
  if (descr === '') return '';
  const type = (itemType ?? '').trim();
  const typeSuffix = type === '' ? '' : ` ${type}`;
  return hasServiceItem
    ? `${descr}${typeSuffix} ${GLYPH_ARROW} `
    : `${descr}${typeSuffix}`;
}

// Prior `itemPrefix` helper was inlined into `ExplainerItemRow`
// so the Δ (position-2 temp-diet) prefix could be applied AFTER
// the composed body — matching the visualizer's order of
// operations (.tsx:161-163) instead of embedding it in the
// position-prefix slot. Both `GLYPH_INCREMENT` and
// `GLYPH_BLACK_SQUARE` are still used at the call site above.

/**
 * Single row inside the Tray Ticket grid. Duplicates WinForms
 * Explainer `gv_CustomDrawCell` (.vb:365-392) which composes the
 * visible cell text as:
 *
 *   ItemPrefix(row)
 * + FormatItemText(Consist)
 * + FormatItemText(Portion)
 * + FormatItemTextWithArrow(Itm_Descr, ItemType, HasServiceItem)
 * + Service_Item
 *
 * Font matches the visualizer's `.ttvc-item`: Times New Roman,
 * 19px, line-height 1.25. Per-side borders honor `BorderSide`
 * bitmask (top=2, bottom=8, both=10) — the same values WinForms
 * draws via `DrawLine` (.vb:375-383).
 */
// Fixed-width gutter reserved on the left of every menu-item row
// for the per-item marker (WinForms `gv` `colType` cell —
// `.vb:322-410`).
const EXPLAINER_MARKER_GUTTER = 75;

// Marker rendered in the gutter. `kind` picks the pill color.
type ExplainerMarkerKind = 'mp' | 'mp-global' | 'adj' | 'adj-global' | 'as';
interface ExplainerMarker {
  text: string;
  kind: ExplainerMarkerKind;
}

// Distinct pill palette per marker kind. Each pair is
// [background, foreground]; foreground stays white for contrast
// on the darker fills, dark on the light AS pill.
const MARKER_PALETTE: Record<ExplainerMarkerKind, { bg: string; fg: string }> = {
  'mp':         { bg: '#1976d2', fg: '#fff' }, // Meal Pattern         — blue
  'mp-global':  { bg: '#7b1fa2', fg: '#fff' }, // Meal Pattern (G)     — purple
  'adj':        { bg: '#ed6c02', fg: '#fff' }, // Adjustment           — orange
  'adj-global': { bg: '#d32f2f', fg: '#fff' }, // Adjustment (G)       — red
  'as':         { bg: '#388e3c', fg: '#fff' }, // Always Served        — green
};

/**
 * Row-shimmer placeholder — mirrors the visualizer's
 * `<MenuItemsShimmer>` (TrayTicketVisualizerDialog.tsx). Eight
 * pulsating gray bars with staggered animation delay so the
 * shimmer reads as text lines instead of a solid block. Used
 * for both the Tray Ticket menu-items pane and the Steps pane
 * while the initial `useTrayTicketExplainer` call is in flight
 * or during a `useRegenerateExplainer` re-run.
 */
function MenuItemsShimmer() {
  const widths = ['92%', '78%', '85%', '70%', '88%', '60%', '80%', '74%'];
  return (
    <Box sx={{ py: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {widths.map((w, i) => (
        <Box
          key={i}
          sx={{
            height: 18,
            width: w,
            borderRadius: '4px',
            bgcolor: 'grey.200',
            animation: 'pulse 1.6s ease-in-out infinite',
            animationDelay: `${i * 0.08}s`,
            '@keyframes pulse': {
              '0%': { opacity: 1 },
              '50%': { opacity: 0.45 },
              '100%': { opacity: 1 },
            },
          }}
        />
      ))}
    </Box>
  );
}

/**
 * WinForms `GenerateAbbreviationString` (.vb:400-408) — takes
 * the first letter of each whitespace-separated word.
 *   "Meal Pattern" → "MP"
 *   "Adjustment"   → "A"
 *   "Sub Adj"      → "SA"
 */
function generateAbbreviation(s: string | null | undefined): string {
  if (!s) return '';
  return s
    .split(/\s+/)
    .map((w) => (w.length > 0 ? w[0] : ''))
    .join('');
}

function ExplainerItemRow({ item, marker, isFocused, onFocus, useCaps, useColor }: {
  item: ExplainerMenuItem;
  marker: ExplainerMarker | null;
  isFocused: boolean;
  onFocus: () => void;
  /** Facility setting `TT_UseCapsOnItems` — uppercases the
   *  composed item line + note text. Passed from the parent so
   *  we only read the store once per render pass. */
  useCaps: boolean;
  /** Facility setting `Use_Color` — gates per-item color from
   *  `TT_ItemColor`. When false, items render in the default
   *  text color regardless of the ARGB value on the row. */
  useColor: boolean;
}) {
  // Category header — thin hairline divider matching the
  // visualizer's `.ttvc-category-separator` (a 50%-width top
  // border, not a bold uppercase heading). Sits FLUSH across
  // the marker gutter + body since it's a page-wide separator.
  if (item.isCategoryHeader) {
    return (
      <Box sx={{
        borderTop: '1px solid #888',
        my: 0.5,
        width: '50%',
        ml: `${EXPLAINER_MARKER_GUTTER}px`,
      }} />
    );
  }

  // WinForms bold-note detection — mealtime / diet-order notes
  // render bold + plain black, NOT italic-orange like a regular
  // item note (matches visualizer .tsx:99-127).
  const isBoldNote = item.isMealtimeNote || item.isDietOrderNote;

  // Item notes — plain text, no prefix / consist / portion /
  // arrow formatting. Match visualizer's isNote branch exactly
  // (.tsx:105-133) — bold notes are flush-left bold black, plain
  // notes are italic-orange indented under their parent item.
  if (item.isNote) {
    const noteText = useCaps
      ? (item.itemDescription ?? '').toUpperCase()
      : (item.itemDescription ?? '');
    if (isBoldNote) {
      return (
        <Box
          onClick={onFocus}
          sx={{
            display: 'flex',
            fontFamily: '"Times New Roman", Times, serif',
            fontSize: 19, lineHeight: 1.25,
            fontWeight: 700,
            color: (useColor && item.itemColor != null)
              ? argbToHex(item.itemColor) : '#000',
            cursor: 'pointer',
            bgcolor: isFocused ? 'rgba(0, 0, 128, 0.10)' : 'transparent',
            '&:hover': { bgcolor: isFocused ? 'rgba(0, 0, 128, 0.14)' : 'rgba(0, 0, 0, 0.04)' },
          }}
        >
          <Box sx={{ flex: `0 0 ${EXPLAINER_MARKER_GUTTER}px` }} />
          <Box sx={{ pl: 0.5 }}>{noteText}</Box>
        </Box>
      );
    }
    return (
      <Box
        onClick={onFocus}
        sx={{
          display: 'flex',
          fontFamily: '"Times New Roman", Times, serif',
          fontSize: 15, fontStyle: 'italic', color: '#d97706',
          cursor: 'pointer',
          bgcolor: isFocused ? 'rgba(0, 0, 128, 0.10)' : 'transparent',
          '&:hover': { bgcolor: isFocused ? 'rgba(0, 0, 128, 0.14)' : 'rgba(0, 0, 0, 0.04)' },
        }}
      >
        <Box sx={{ flex: `0 0 ${EXPLAINER_MARKER_GUTTER}px` }} />
        <Box sx={{ pl: 3 }}>{noteText}</Box>
      </Box>
    );
  }

  // Regular row composer — mirrors visualizer's `ItemRow`
  // (.tsx:135-164). Order:
  //   • Default: prefix + consist + portion + itmDescr [itemType → serviceItem]
  //   • FKey_Prd_Portion === PKEY_PRD_PORTION_NO: portion-first (portion + consist ...)
  //   • Overridden item → append "  ●  OK"
  //   • Position=2 + MealPatternModifiedByTempDiet → prepend "∆ "
  //   • useCaps → whole line uppercased last
  const hasService = ((item.serviceItem ?? '').trim().length > 0);
  const portionFirst = item.fKey_Prd_Portion === PKEY_PRD_PORTION_NO;
  // Position prefix (WinForms `ItemPrefix`) — same as before but
  // the Δ increment for temp-diet-modified position-2 rows is
  // now applied AFTER the composed body (matching the visualizer
  // .tsx:161-163) rather than baked into the position prefix.
  const positionPrefix = (item.position === 3)
    ? `${GLYPH_BLACK_SQUARE} `
    : (item.position === 4 ? `${GLYPH_BLACK_SQUARE} ${GLYPH_BLACK_SQUARE} ` : '');
  const dietSlots = portionFirst
    ? formatItemText(item.portion) + formatItemText(item.consistency)
    : formatItemText(item.consistency) + formatItemText(item.portion);
  let body = ''
    + positionPrefix
    + dietSlots
    + formatItemTextWithArrow(item.itemDescription, item.itemType, hasService)
    + (item.serviceItem ?? '').trim();
  if (item.overriddenItem) body += `  ${GLYPH_BLACK_CIRCLE}  OK`;
  if (item.position === 2 && item.mealPatternModifiedByTempDiet) {
    body = `${GLYPH_INCREMENT} ${body}`;
  }
  const line = useCaps ? body.toUpperCase() : body;

  // Per-side border via `BorderSide` bitmask — matches visualizer
  // `borderStyle` (.tsx:47-56). Uses `Argb_BoxColor` for the
  // stroke, falling back to black. All FOUR sides supported so
  // consecutive items whose borders align form visual boxes
  // naturally (identical to WinForms `RenderMenu` behavior).
  const boxStroke = boxColorToHex(item.boxColor) ?? '#000';
  const borderStyle: React.CSSProperties = {};
  if (item.borderSide & BORDER_TOP)    borderStyle.borderTop    = `1px solid ${boxStroke}`;
  if (item.borderSide & BORDER_BOTTOM) borderStyle.borderBottom = `1px solid ${boxStroke}`;
  if (item.borderSide & BORDER_LEFT)   borderStyle.borderLeft   = `1px solid ${boxStroke}`;
  if (item.borderSide & BORDER_RIGHT)  borderStyle.borderRight  = `1px solid ${boxStroke}`;

  // Item text color — gated on facility `Use_Color`. Same rule
  // as the visualizer (.tsx:137-139).
  const colorStyle: React.CSSProperties = {};
  if (useColor && item.itemColor != null) {
    colorStyle.color = argbToHex(item.itemColor);
  }

  return (
    <Box
      onClick={onFocus}
      sx={{
        // Match visualizer's `.ttvc-item` exactly so the two
        // dialogs' tray-body text reads at the same weight.
        fontFamily: '"Times New Roman", Times, serif',
        fontSize: 19,
        lineHeight: 1.25,
        color: 'text.primary',
        display: 'flex',
        alignItems: 'baseline',
        cursor: 'pointer',
        // Focused-row backdrop — same navy tint used on the
        // step tree so the linkage between grid focus and tree
        // highlights is visually obvious.
        bgcolor: isFocused ? 'rgba(0, 0, 128, 0.10)' : 'transparent',
        transition: 'background-color 90ms ease-out',
        '&:hover': {
          bgcolor: isFocused ? 'rgba(0, 0, 128, 0.14)' : 'rgba(0, 0, 0, 0.04)',
        },
      }}
      style={{ ...borderStyle }}
    >
      {/* Marker gutter — WinForms `colType` cell. Rendered as a
          colored pill with a distinct hue per marker kind (MP /
          MP G / A / A G / AS). Pill only appears when there IS
          a marker for this item; otherwise the gutter stays
          blank but still reserves its 75px so text stays
          aligned. */}
      <Box sx={{
        flex: `0 0 ${EXPLAINER_MARKER_GUTTER}px`,
        pl: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
      }}>
        {marker && (
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              px: 0.75,
              py: 0.125,
              borderRadius: '10px',
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
              fontWeight: 700,
              lineHeight: '1.4',
              bgcolor: MARKER_PALETTE[marker.kind].bg,
              color: MARKER_PALETTE[marker.kind].fg,
              whiteSpace: 'nowrap',
              letterSpacing: 0.3,
            }}
          >
            {marker.text}
          </Box>
        )}
      </Box>
      <Box sx={{ flex: '1 1 auto', px: 0.5, display: 'flex', alignItems: 'baseline' }}>
        <Box component="span" style={colorStyle} sx={{ fontWeight: isBoldNote ? 700 : undefined }}>
          {line}
        </Box>
        {/* WinForms substitution suffix — italic + secondary
            color, matches visualizer's `.ttvc-item-consist`
            treatment (.css:160-165). */}
        {item.isSubstitution && item.substitutionReason && (
          <Box
            component="span"
            sx={{
              ml: 1,
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
              fontStyle: 'italic',
              color: 'text.secondary',
            }}
          >
            [{item.substitutionReason}]
          </Box>
        )}
      </Box>
    </Box>
  );
}

/**
 * Bottom-left pane — food-group + allergy summary for the
 * currently-focused menu-item row. Duplicates the WinForms
 * `RefreshItemFoodGroupAndAllergies`
 * (frmDietech_Prd_TrayTicket_Explain.vb:1425) layout:
 *
 *   ┌──────────────────────────────────────┬───────────┐
 *   │ Food Groups:                         │  Item Name│  ← headers row
 *   │   Meat, Beef, Ground                 │           │
 *   │   Dairy, Cheese                      │           │
 *   │ Allergy Groups:                      │           │
 *   │   Dairy                              │           │
 *   │   Eggs                               │           │
 *   └──────────────────────────────────────┴───────────┘
 *
 * Empty (`null`) `details` → blank pane, matching WinForms
 * `If row Is Nothing Then Return`. Non-null but empty
 * food/allergy arrays render the "No … Assigned" fallback
 * headers, same as WinForms .vb:1462-1465 and 1520-1522.
 */
function ExplainerFoodGroupAllergy({ details, hasFocus }: {
  details: import('../../types/trayTicket').ExplainerItemFoodGroupAllergies | null;
  /** True when the user has focused a row — used to distinguish
   *  the "no row focused" empty state (matches WinForms `If row Is
   *  Nothing Then Return`) from the "focus is set but the server
   *  didn't return details for this item" state (usually means
   *  the API hasn't been restarted after adding item-detail
   *  support). */
  hasFocus: boolean;
}) {
  if (!hasFocus) {
    return (
      <Typography sx={{
        fontFamily: '"Times New Roman", Times, serif',
        fontSize: 15, fontStyle: 'italic', color: 'text.secondary',
      }}>
        Click an item above to see its food groups and allergies.
      </Typography>
    );
  }
  if (!details) {
    return (
      <Typography sx={{
        fontFamily: '"Times New Roman", Times, serif',
        fontSize: 15, fontStyle: 'italic', color: 'text.disabled',
      }}>
        No food-group / allergy data available for this item.
      </Typography>
    );
  }
  const hasFG = details.foodGroups.length > 0;
  const hasAG = details.allergies.length > 0;
  // Accent color for section headers — matches WinForms
  // `GetLabelHighlightColorFromSkin(...)` (accent blue). MUI
  // primary.main renders the same weight against both light
  // and dark grounds.
  const headerColor = 'primary.main';
  return (
    <Box sx={{
      fontFamily: '"Times New Roman", Times, serif',
      fontSize: 15,
      lineHeight: 1.35,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.75,
    }}>
      {/* Right-aligned item name (WinForms `labelHeader` at
          .vb:1434-1445). Rendered as a small caption band flush
          right, matching the 200px-wide right-aligned label. */}
      {details.itemDescription && (
        <Typography sx={{
          fontFamily: '"Times New Roman", Times, serif',
          fontSize: 15, fontWeight: 700, textAlign: 'right',
          color: 'text.primary',
          borderBottom: '1px solid var(--border-color, #e0e0e0)',
          pb: 0.25,
        }}>
          {details.itemDescription}
          {details.hasRecipe && (
            <Box component="span" sx={{
              ml: 1, fontSize: 11, fontWeight: 400, fontStyle: 'italic',
              color: 'text.secondary',
            }}>
              (recipe)
            </Box>
          )}
        </Typography>
      )}

      {/* Food-group block */}
      <Box>
        <Typography sx={{
          fontFamily: '"Times New Roman", Times, serif',
          fontSize: 15, fontWeight: 700, color: headerColor,
        }}>
          {hasFG ? 'Food Groups:' : 'No Food Groups Assigned'}
        </Typography>
        {hasFG && (
          <Box sx={{ pl: 2, whiteSpace: 'pre-line' }}>
            {details.foodGroups.map((chain, i) => (
              <Box key={`fg-${i}`}>{chain}</Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Allergy-group block */}
      <Box>
        <Typography sx={{
          fontFamily: '"Times New Roman", Times, serif',
          fontSize: 15, fontWeight: 700, color: headerColor,
        }}>
          {hasAG ? 'Allergy Groups:' : 'No Allergy Groups Assigned'}
          {details.hasRecipe && hasAG && (
            <Box component="span" sx={{
              ml: 1, fontSize: 11, fontWeight: 400, fontStyle: 'italic',
              color: 'text.secondary',
            }}>
              (incl. ingredients)
            </Box>
          )}
        </Typography>
        {hasAG && (
          <Box sx={{ pl: 2, whiteSpace: 'pre-line' }}>
            {details.allergies.map((name, i) => (
              <Box key={`ag-${i}`}>{name}</Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

/**
 * Recursive step-tree list. WinForms `trl` TreeList collapses
 * to a nested checkbox list in web-land — hierarchy comes from
 * `parentId` on each `ExplainerStepNode`. Indent bumps 16px
 * per level. Root nodes render bold to separate categories
 * (Diet Order / Meal Pattern / Adjustments) from their children.
 */
function ExplainerStepList({
  nodes, checked, dietOrderChecked, disabled, onToggle,
  activeNodeIds, mpAdjDisabled,
}: {
  nodes: ExplainerStepNode[];
  checked: Set<number>;
  /** WinForms `UseResidentDT()` — when Diet Order root is
   *  UNCHECKED, MP + Adj checkboxes are disabled (`.vb:997`). */
  dietOrderChecked: boolean;
  disabled: boolean;
  onToggle: (n: ExplainerStepNode) => void;
  /** Node ids that the focused menu-item row triggered.
   *  Mirrors WinForms `lstNodesActive` — matched nodes get a
   *  translucent navy background (.vb:636-638). */
  activeNodeIds: Set<number>;
  /** WinForms `SetMPAdjDisabled()` — true when the whole MP/Adj
   *  subtree should render disabled. Painted `WhiteSmoke`
   *  (.vb:641-643). */
  mpAdjDisabled: boolean;
}) {
  // Δ prefix is NOT added here — the generator embeds it into
  // the PatientInfo `DietType_{i}` values when the diet is
  // overridden. Server reads those verbatim into the Diet Order
  // child's description, so the Δ (if present) is already in
  // `n.description`.
  // Build parent → children map.
  const childrenOf = useMemo(() => {
    const map = new Map<number | null, ExplainerStepNode[]>();
    for (const n of nodes) {
      const key = n.parentId;
      const arr = map.get(key) ?? [];
      arr.push(n);
      map.set(key, arr);
    }
    return map;
  }, [nodes]);

  // Node classification — mirrors WinForms `dtSteps` shape:
  //   • Root headers    : id ∈ {1,2,3}, parentId=null       — Diet Order / MP / Adj bold headers
  //   • Diet Order child: stepType=1, parentId=1             — the "Δ REGULAR,PUREE" row
  //   • Adj sub-headers : stepType 4-14, fKey_Adj_MP=null    — "Diet Override" etc. (no checkbox)
  //   • Leaves (MP/Adj) : fKey_Adj_MP != null                — checkbox + toggles include list
  const nodeMeta = (n: ExplainerStepNode) => {
    const isRootHeader = n.parentId == null;
    const isDietOrderRoot = n.id === 1;
    const isMPRoot = n.id === 2;
    const isAdjRoot = n.id === 3;
    const isDietOrderChild = n.stepType === 1 && n.parentId === 1;
    const isMPLeaf = n.stepType === 2 && n.fKey_Adj_MP != null;
    const isAdjLeaf = n.stepType >= 4 && n.stepType <= 14 && n.fKey_Adj_MP != null;
    const isAdjSubHeader = n.stepType >= 4 && n.stepType <= 14 && n.fKey_Adj_MP == null;
    return { isRootHeader, isDietOrderRoot, isMPRoot, isAdjRoot, isDietOrderChild, isMPLeaf, isAdjLeaf, isAdjSubHeader };
  };

  // Aggregate the checked state of any subtree's leaves — used
  // by root headers AND Adj sub-headers to render a three-state
  // checkbox (all / some / none) that reflects their descendants.
  const subtreeAggregate = (rootId: number): 'none' | 'some' | 'all' => {
    const leaves: ExplainerStepNode[] = [];
    const walk = (id: number) => {
      const kids = childrenOf.get(id) ?? [];
      for (const k of kids) {
        const m = nodeMeta(k);
        if (m.isMPLeaf || m.isAdjLeaf || m.isDietOrderChild) leaves.push(k);
        walk(k.id);
      }
    };
    walk(rootId);
    if (leaves.length === 0) return 'none';
    let count = 0;
    for (const l of leaves) {
      const meta = nodeMeta(l);
      const isCk = meta.isDietOrderChild
        ? dietOrderChecked
        : (l.fKey_Adj_MP != null && checked.has(l.fKey_Adj_MP));
      if (isCk) count++;
    }
    if (count === 0) return 'none';
    if (count === leaves.length) return 'all';
    return 'some';
  };

  const renderNode = (n: ExplainerStepNode, depth: number): React.ReactNode => {
    const kids = childrenOf.get(n.id) ?? [];
    const m = nodeMeta(n);

    // Every node except pure separators gets a checkbox. Adj
    // sub-headers ("Diet Override" / "Modify" / etc.) also get
    // checkboxes that reflect the aggregate state of their leaf
    // descendants — matches WinForms which shows a checkbox on
    // every tree row.
    const isCheckable = m.isRootHeader || m.isDietOrderChild
      || m.isMPLeaf || m.isAdjLeaf || m.isAdjSubHeader;
    // Aggregate state for root headers + Adj sub-headers; direct
    // state for Diet Order child + leaves.
    let isChecked = false;
    let isIndeterminate = false;
    if (m.isRootHeader || m.isAdjSubHeader) {
      const agg = subtreeAggregate(n.id);
      isChecked = agg === 'all';
      isIndeterminate = agg === 'some';
    } else if (m.isDietOrderChild) {
      isChecked = dietOrderChecked;
    } else if (n.fKey_Adj_MP != null) {
      isChecked = checked.has(n.fKey_Adj_MP);
    }
    // MP / Adj leaves + Adj sub-headers get gated when Diet Order
    // is unchecked. Roots stay clickable so the user can toggle
    // Diet Order back on from either row.
    const gatedByDietOrder = (m.isMPLeaf || m.isAdjLeaf) && !dietOrderChecked;
    // Bold: root headers + Adj sub-headers ("Diet Override" etc.).
    const isBold = m.isRootHeader || m.isAdjSubHeader;

    // Is this a member of the MP/Adj subtree? Roots (2, 3),
    // their descendants (any node whose parent chain hits 2 or
    // 3), and sub-headers under those. Diet Order (id 1, 1000)
    // is never grayed by SetMPAdjDisabled.
    const isMPAdjSubtree = m.isMPRoot || m.isAdjRoot
      || m.isMPLeaf || m.isAdjLeaf || m.isAdjSubHeader;
    // WinForms priority: DISABLED gray wins over ACTIVE navy
    // (.vb:641-644 assigns BackColor AFTER the navy branch, so
    // it clobbers). Reproduce here — disabled first, then navy.
    const isActive = activeNodeIds.has(n.id);
    const isDisabledSubtree = mpAdjDisabled && isMPAdjSubtree;
    let rowBg: string = 'transparent';
    if (isDisabledSubtree) {
      // WinForms `Color.WhiteSmoke` = #F5F5F5.
      rowBg = '#f5f5f5';
    } else if (isActive) {
      // WinForms `Color.FromArgb(50, Navy)` — alpha 50/255 ≈ 0.20.
      rowBg = 'rgba(0, 0, 128, 0.20)';
    }

    return (
      <Box key={n.id}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          pl: `${depth * 20}px`,
          py: 0.25,
          fontSize: 13,
          fontWeight: isBold ? 700 : 400,
          color: (n.isDisabled || gatedByDietOrder || isDisabledSubtree) ? 'text.disabled' : 'text.primary',
          bgcolor: rowBg,
          transition: 'background-color 90ms ease-out',
        }}>
          {isCheckable ? (
            <Checkbox
              size="small"
              checked={isChecked}
              indeterminate={isIndeterminate}
              disabled={disabled || n.isDisabled || gatedByDietOrder}
              onChange={() => onToggle(n)}
              sx={{ p: 0.25 }}
            />
          ) : (
            <Box sx={{ width: 24 }} />
          )}
          {/* Flag glyph — WinForms tlcFlag column paints
              imgMisc.Images(0) (small warning) when the MP's
              category isn't in the tray-ticket print list
              (.vb:646-651). Only applies to MP nodes; other
              node types always report false. */}
          {n.menuCategoryNotInPrintList && (
            <Tooltip title="This item is not currently marked to print on tray tickets.">
              <Box
                component="i"
                className="bi bi-exclamation-triangle-fill"
                sx={{
                  color: '#d97706',
                  fontSize: 13,
                  lineHeight: 1,
                  display: 'inline-block',
                  textOverflow: 'clip',
                }}
              />
            </Tooltip>
          )}
          <span>{n.description}</span>
        </Box>
        {kids.map((k) => renderNode(k, depth + 1))}
      </Box>
    );
  };

  const roots = childrenOf.get(null) ?? [];
  return <>{roots.map((r) => renderNode(r, 0))}</>;
}
